import { useState } from "react";
import { Link } from "react-router-dom";
import { Button, formatShort } from "@project/ui";
import { DashboardBackLink } from "@apps/dashboard/components/layout/DashboardBackLink";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { authClient } from "@apps/dashboard/lib/auth-client";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { translateAuthError } from "@apps/dashboard/features/auth/map-auth-error";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { isPasskeyAuthEnabled } from "@apps/dashboard/lib/env";

type PasskeyRow = {
  id: string;
  name?: string | null;
  deviceType?: string | null;
  createdAt?: string | Date | null;
};

export default function SettingsPasskeysPage(): JSX.Element {
  const { t } = useI18n();
  const listState = authClient.useListPasskeys();
  const { data, error, isPending, refetch } = listState;

  const [actionError, setActionError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);

  const passkeys = Array.isArray(data) ? (data as PasskeyRow[]) : [];

  async function handleAdd(): Promise<void> {
    setActionError(null);
    setAdding(true);
    try {
      const result = await authClient.passkey.addPasskey({
        authenticatorAttachment: "platform",
      });
      if (result.error) {
        setActionError(translateAuthError(result.error, t));
        return;
      }
      await refetch();
    } catch (unknownErr) {
      setActionError(translateAuthError(unknownErr, t));
    } finally {
      setAdding(false);
    }
  }

  async function handleDelete(id: string): Promise<void> {
    setActionError(null);
    setDeletingId(id);
    try {
      const result = await authClient.passkey.deletePasskey({ id });
      if (result.error) {
        setActionError(translateAuthError(result.error, t));
        return;
      }
      await refetch();
    } catch (unknownErr) {
      setActionError(translateAuthError(unknownErr, t));
    } finally {
      setDeletingId(null);
    }
  }

  function openRename(row: PasskeyRow): void {
    setActionError(null);
    setRenameId(row.id);
    setRenameValue(typeof row.name === "string" && row.name.trim() ? row.name.trim() : "");
  }

  async function handleRenameSave(): Promise<void> {
    if (!renameId) return;
    const name = renameValue.trim();
    if (!name) {
      setActionError(t("dashboardUi.settingsPasskeysPage.renameEmpty"));
      return;
    }
    setRenaming(true);
    setActionError(null);
    try {
      const result = await authClient.passkey.updatePasskey({ id: renameId, name });
      if (result.error) {
        setActionError(translateAuthError(result.error, t));
        return;
      }
      setRenameId(null);
      setRenameValue("");
      await refetch();
    } catch (unknownErr) {
      setActionError(translateAuthError(unknownErr, t));
    } finally {
      setRenaming(false);
    }
  }

  if (!isPasskeyAuthEnabled()) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <DashboardBackLink to={dashboardPaths.settings}>{t("dashboardUi.settingsHub.backToAccount")}</DashboardBackLink>
        <h1 className="font-headline text-2xl font-bold tracking-tight text-primary">
          {t("dashboardUi.pages.settingsPasskeys")}
        </h1>
        <p className="mt-4 font-body text-body-md text-on-surface-variant">{t("dashboardUi.settingsPasskeysPage.disabledHint")}</p>
        <Link
          to={dashboardPaths.settings}
          className="mt-6 inline-block font-semibold text-primary hover:underline"
        >
          {t("dashboardUi.settingsHub.backToAccount")}
        </Link>
      </div>
    );
  }

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <DashboardBackLink to={dashboardPaths.settings}>{t("dashboardUi.settingsHub.backToAccount")}</DashboardBackLink>
      <h1 className="font-headline text-2xl font-bold tracking-tight text-primary">
        {t("dashboardUi.pages.settingsPasskeys")}
      </h1>
      <p className="mt-2 font-body text-body-md text-on-surface-variant">{t("dashboardUi.settingsPasskeysPage.subtitle")}</p>

      <div className="mt-8 rounded-2xl border border-outline-variant/15 bg-surface-container-lowest p-6 shadow-sm sm:p-8">
        {actionError ? (
          <div
            className="mb-4 rounded-lg bg-error/10 px-4 py-3 text-body-md text-error outline outline-1 outline-error/20"
            role="alert"
          >
            {actionError}
          </div>
        ) : null}

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-body text-body-sm text-on-surface-variant">{t("dashboardUi.settingsPasskeysPage.addHint")}</p>
          <Button type="button" disabled={adding} onClick={() => void handleAdd()}>
            {adding ? t("dashboardUi.settingsPasskeysPage.adding") : t("dashboardUi.settingsPasskeysPage.addButton")}
          </Button>
        </div>

        <div className="mt-8 border-t border-outline-variant/15 pt-8">
          {isPending ? (
            <p className="font-body text-body-md text-on-surface-variant">{t("dashboardUi.settingsPasskeysPage.loading")}</p>
          ) : error ? (
            <p className="font-body text-body-md text-error" role="alert">
              {t("dashboardUi.settingsPasskeysPage.loadError")}
            </p>
          ) : passkeys.length === 0 ? (
            <p className="font-body text-body-md text-on-surface-variant">{t("dashboardUi.settingsPasskeysPage.empty")}</p>
          ) : (
            <ul className="space-y-4">
              {passkeys.map((row) => (
                <li
                  key={row.id}
                  className="flex flex-col gap-3 rounded-xl border border-outline-variant/20 bg-surface-container-low/40 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-body text-body-md font-semibold text-on-surface">
                      {typeof row.name === "string" && row.name.trim()
                        ? row.name.trim()
                        : t("dashboardUi.settingsPasskeysPage.unnamedPasskey")}
                    </p>
                    <p className="mt-1 font-body text-xs text-on-surface-variant">
                      {row.deviceType
                        ? t("dashboardUi.settingsPasskeysPage.deviceTypeLabel", { type: row.deviceType })
                        : null}
                      {row.createdAt ? (
                        <>
                          {row.deviceType ? " · " : null}
                          {t("dashboardUi.settingsPasskeysPage.createdLabel")}{" "}
                          {formatShort(
                            typeof row.createdAt === "string"
                              ? row.createdAt
                              : row.createdAt instanceof Date
                                ? row.createdAt.toISOString()
                                : String(row.createdAt),
                          )}
                        </>
                      ) : null}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => openRename(row)}>
                      {t("dashboardUi.settingsPasskeysPage.renameButton")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-error hover:bg-error/10"
                      disabled={deletingId === row.id}
                      onClick={() => {
                        if (window.confirm(t("dashboardUi.settingsPasskeysPage.deleteConfirm"))) {
                          void handleDelete(row.id);
                        }
                      }}
                    >
                      {deletingId === row.id ? t("dashboardUi.settingsPasskeysPage.deleting") : t("dashboardUi.settingsPasskeysPage.deleteButton")}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {renameId ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="passkey-rename-title"
          onClick={() => {
            if (!renaming) {
              setRenameId(null);
              setRenameValue("");
            }
          }}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="passkey-rename-title" className="font-headline text-lg font-semibold text-primary">
              {t("dashboardUi.settingsPasskeysPage.renameDialogTitle")}
            </h2>
            <label htmlFor="passkey-rename-input" className="mt-4 block font-body text-sm font-medium text-on-surface">
              {t("dashboardUi.settingsPasskeysPage.renameLabel")}
            </label>
            <input
              id="passkey-rename-input"
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              className="mt-2 w-full rounded-lg border border-outline-variant/30 bg-surface px-3 py-2 font-body text-body-md text-on-surface outline-none ring-primary focus:ring-2"
              autoComplete="off"
            />
            <div className="mt-6 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={renaming}
                onClick={() => {
                  setRenameId(null);
                  setRenameValue("");
                }}
              >
                {t("common.cancel")}
              </Button>
              <Button type="button" disabled={renaming} onClick={() => void handleRenameSave()}>
                {renaming ? t("dashboardUi.profile.saving") : t("dashboardUi.settingsPasskeysPage.renameSave")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
